'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  activateBusinessRevenueSource,
  createBusinessRevenueSource,
  deactivateBusinessRevenueSource,
  getBusinessReportCenters,
  getBusinessRevenueSources,
  updateBusinessRevenueSource,
  type BusinessReportCenter,
  type BusinessRevenueSource,
  type BusinessRevenueSourceInput,
  type BusinessRevenueSourceMode,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';
import {
  cleanSpaces,
  errorMessage,
  exportCsv,
  FinanceField,
  FinanceModal,
  FinanceStatus,
  label,
} from './BusinessReportFinanceUi';

type SourceForm = {
  sourceKey: string;
  sourceName: string;
  description: string;
  sourceMode: BusinessRevenueSourceMode;
  displayOrder: string;
  validationConfiguration: string;
  centerCodes: string[];
};

const EMPTY_FORM: SourceForm = {
  sourceKey: '',
  sourceName: '',
  description: '',
  sourceMode: 'AUTOMATIC',
  displayOrder: '100',
  validationConfiguration: JSON.stringify({
    activationState: 'BLOCKED_PENDING_FINANCE_DEFINITION',
    approvalReference: '',
  }, null, 2),
  centerCodes: [],
};

export function BusinessRevenueSourcesTab({ active, token, onTopBarActionsChange }: { active: boolean; token?: string; onTopBarActionsChange?: (actions: ReactNode) => void }) {
  const [sources, setSources] = useState<BusinessRevenueSource[]>([]);
  const [centers, setCenters] = useState<BusinessReportCenter[]>([]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<BusinessRevenueSource | null | undefined>(undefined);
  const [form, setForm] = useState<SourceForm>(EMPTY_FORM);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sourceRows, centerRows] = await Promise.all([
        getBusinessRevenueSources(token, { search, sourceMode: mode, status }),
        getBusinessReportCenters(token, { status: 'ACTIVE' }),
      ]);
      setSources(sourceRows);
      setCenters(centerRows.filter((center) => center.approvalStatus === 'APPROVED' && center.accessCenterName.toLowerCase() !== 'management'));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Revenue Sources', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [mode, search, status, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const counts = useMemo(() => ({
    total: sources.length,
    active: sources.filter((source) => source.isActive).length,
    automatic: sources.filter((source) => source.sourceMode === 'AUTOMATIC').length,
    manual: sources.filter((source) => source.sourceMode === 'MANUAL').length,
  }), [sources]);

  const openCreate = useCallback(() => {
    setForm({ ...EMPTY_FORM, centerCodes: centers.map((center) => center.centerCode) });
    setEditing(null);
  }, [centers]);

  const download = useCallback(() => exportCsv(
    'business-revenue-sources.csv',
    ['Source Key', 'Source Name', 'Mode', 'Status', 'Display Order', 'Applicable Centres', 'Activation State', 'Approval Reference'],
    sources.map((source) => [
      source.sourceKey,
      source.sourceName,
      source.sourceMode,
      source.isActive ? 'ACTIVE' : 'INACTIVE',
      source.displayOrder,
      source.applicableCenters.filter((mapping) => mapping.isActive).map((mapping) => mapping.center.displayName).join('; '),
      String(source.validationConfiguration.activationState || ''),
      String(source.validationConfiguration.approvalReference || ''),
    ]),
  ), [sources]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(<><button className="btn btn-outline compact-btn" onClick={download} disabled={!sources.length}>Export CSV</button><button className="btn btn-primary compact-btn" onClick={openCreate}>+ Add Revenue Source</button></>);
    return () => onTopBarActionsChange(null);
  }, [active, download, onTopBarActionsChange, openCreate, sources.length]);

  if (!active) return null;

  const openEdit = (source: BusinessRevenueSource) => {
    setForm({
      sourceKey: source.sourceKey,
      sourceName: source.sourceName,
      description: source.description || '',
      sourceMode: source.sourceMode,
      displayOrder: String(source.displayOrder),
      validationConfiguration: JSON.stringify(source.validationConfiguration || {}, null, 2),
      centerCodes: source.applicableCenters.filter((mapping) => mapping.isActive).map((mapping) => mapping.center.centerCode),
    });
    setEditing(source);
  };

  const save = async () => {
    setLoading(true);
    try {
      const input = sourceInput(form);
      if (editing) await updateBusinessRevenueSource(editing.sourceKey, input, token);
      else await createBusinessRevenueSource(input, token);
      setEditing(undefined);
      await load();
      setAlert({
        type: 'success',
        title: editing ? 'Revenue Source updated' : 'Revenue Source created',
        message: 'The source was saved inactive. Activation still requires an approved policy reference and explicit confirmation.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save Revenue Source', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (source: BusinessRevenueSource) => {
    const action = source.isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${label(action)} ${source.sourceName}? This change is audited.`)) return;
    setLoading(true);
    try {
      if (source.isActive) await deactivateBusinessRevenueSource(source.sourceKey, token);
      else await activateBusinessRevenueSource(source.sourceKey, token);
      await load();
      setAlert({ type: 'success', title: `Revenue Source ${action}d`, message: `${source.sourceName} is now ${source.isActive ? 'inactive' : 'active'}.` });
    } catch (error) {
      setAlert({ type: 'error', title: `Could not ${action} Revenue Source`, message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-revenue-sources">
      {loading && <Loader message="Processing Revenue Source Master..." />}
      <div className="business-finance-kpis">
        <div><span>Total</span><strong>{counts.total}</strong></div>
        <div><span>Active</span><strong>{counts.active}</strong></div>
        <div><span>Automatic</span><strong>{counts.automatic}</strong></div>
        <div><span>Manual</span><strong>{counts.manual}</strong></div>
      </div>
      <div className="enterprise-card">
        <div className="business-finance-filters">
          <FinanceField label="Search"><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Key, name or description" /></FinanceField>
          <FinanceField label="Mode"><select value={mode} onChange={(event) => setMode(event.target.value)}><option value="ALL">All modes</option><option value="AUTOMATIC">Automatic</option><option value="MANUAL">Manual</option></select></FinanceField>
          <FinanceField label="Status"><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="ALL">All statuses</option><option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option></select></FinanceField>
          <button className="btn btn-outline" onClick={() => void load()}>Refresh</button>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table">
            <thead><tr><th>Order</th><th>Revenue Source</th><th>Mode</th><th>Centres</th><th>Policy State</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.id}>
                  <td>{source.displayOrder}</td>
                  <td><strong>{source.sourceName}</strong><small className="business-finance-subline">{source.sourceKey}</small></td>
                  <td><FinanceStatus value={source.sourceMode} /></td>
                  <td>{source.applicableCenters.filter((mapping) => mapping.isActive).length}</td>
                  <td>{String(source.validationConfiguration.activationState || 'NOT SET')}</td>
                  <td><FinanceStatus value={source.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                  <td><div className="business-finance-row-actions"><button className="btn btn-outline btn-sm" onClick={() => openEdit(source)}>Edit</button><button className={`btn btn-sm ${source.isActive ? 'btn-outline' : 'btn-primary'}`} onClick={() => void toggleActive(source)}>{source.isActive ? 'Deactivate' : 'Activate'}</button></div></td>
                </tr>
              ))}
              {!sources.length && <tr><td colSpan={7} className="empty-cell">No Revenue Sources match the selected filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {editing !== undefined && (
        <FinanceModal
          title={editing ? 'Edit Revenue Source' : 'Add Revenue Source'}
          subtitle="Saving changes deactivates the source until it is reviewed and explicitly activated."
          wide
          onClose={() => setEditing(undefined)}
          footer={<><span>UUID and source key remain stable after creation.</span><div><button className="btn btn-outline" onClick={() => setEditing(undefined)}>Cancel</button><button className="btn btn-primary" onClick={() => void save()}>Save Revenue Source</button></div></>}
        >
          <div className="business-source-form-grid">
            <FinanceField label="Source Key" help="Immutable governed key."><input value={form.sourceKey} disabled={Boolean(editing)} onChange={(event) => setForm((current) => ({ ...current, sourceKey: event.target.value.toUpperCase() }))} /></FinanceField>
            <FinanceField label="Source Name"><input value={form.sourceName} onChange={(event) => setForm((current) => ({ ...current, sourceName: event.target.value }))} /></FinanceField>
            <FinanceField label="Mode"><select value={form.sourceMode} onChange={(event) => setForm((current) => ({ ...current, sourceMode: event.target.value as BusinessRevenueSourceMode }))}><option value="AUTOMATIC">Automatic</option><option value="MANUAL">Manual</option></select></FinanceField>
            <FinanceField label="Display Order"><input type="number" min="0" max="10000" value={form.displayOrder} onChange={(event) => setForm((current) => ({ ...current, displayOrder: event.target.value }))} /></FinanceField>
            <FinanceField label="Description" full><textarea rows={3} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} /></FinanceField>
            <FinanceField label="Validation and Approval Policy JSON" help='Activation requires {"activationState":"APPROVED","approvalReference":"..."}; secret/credential keys are rejected.' full><textarea rows={8} spellCheck={false} value={form.validationConfiguration} onChange={(event) => setForm((current) => ({ ...current, validationConfiguration: event.target.value }))} /></FinanceField>
            <fieldset className="business-finance-centers">
              <legend>Applicable Clinical Centres</legend>
              {centers.map((center) => <label key={center.id}><input type="checkbox" checked={form.centerCodes.includes(center.centerCode)} onChange={(event) => setForm((current) => ({ ...current, centerCodes: event.target.checked ? [...current.centerCodes, center.centerCode] : current.centerCodes.filter((code) => code !== center.centerCode) }))} /> <span>{center.displayName} ({center.centerCode})</span></label>)}
            </fieldset>
          </div>
        </FinanceModal>
      )}
      {alert && <AdminAlertModal title={alert.title} message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
    </section>
  );
}

function sourceInput(form: SourceForm): BusinessRevenueSourceInput {
  let configuration: unknown;
  try {
    configuration = JSON.parse(form.validationConfiguration || '{}');
  } catch {
    throw new Error('Validation and Approval Policy must contain valid JSON.');
  }
  if (!configuration || typeof configuration !== 'object' || Array.isArray(configuration)) {
    throw new Error('Validation and Approval Policy must be a JSON object.');
  }
  const displayOrder = Number(form.displayOrder);
  if (!Number.isInteger(displayOrder) || displayOrder < 0) throw new Error('Display order must be a non-negative whole number.');
  if (!cleanSpaces(form.sourceKey) || !cleanSpaces(form.sourceName)) throw new Error('Source key and source name are required.');
  return {
    sourceKey: cleanSpaces(form.sourceKey).toUpperCase().replace(/[^A-Z0-9]+/g, '_'),
    sourceName: cleanSpaces(form.sourceName),
    description: cleanSpaces(form.description) || null,
    sourceMode: form.sourceMode,
    displayOrder,
    validationConfiguration: configuration as Record<string, unknown>,
    centerCodes: [...new Set(form.centerCodes)],
  };
}
